import React, { useEffect, useMemo, useState } from 'react';
import { investmentApi } from '../services/api';
import { Investment, InvestmentType } from '../types';
import Alert from './Alert';
import DateInput from './DateInput';

interface InvestmentEditModalProps {
    investment: Investment;
    onSuccess: () => void;
    onCancel: () => void;
}

const InvestmentEditModal: React.FC<InvestmentEditModalProps> = ({ investment, onSuccess, onCancel }) => {
    const [form, setForm] = useState<{ amount: number; occurredAt: string; type: InvestmentType }>({
        amount: 0,
        occurredAt: '',
        type: 'credit',
    });
    const [amountInput, setAmountInput] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const formatBRL = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    const parseToNumber = (raw: string) => {
        const onlyDigits = raw.replace(/\D/g, '');
        if (!onlyDigits) return 0;
        const cents = parseInt(onlyDigits, 10);
        return cents / 100;
    };

    useEffect(() => {
        const reais = (investment.amount || 0) / 100;
        setAmountInput(reais ? formatBRL(reais) : '');
        // Handle occurredAt format - could be "2025-11" or full ISO
        let occurredAtValue = investment.occurredAt;
        if (occurredAtValue.match(/^\d{4}-\d{2}$/)) {
            // Convert "2025-11" to first day of month in ISO
            const [year, month] = occurredAtValue.split('-');
            occurredAtValue = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, 1, 0, 0, 0, 0)).toISOString();
        }
        setForm({
            amount: reais,
            occurredAt: occurredAtValue,
            type: investment.type,
        });
    }, [investment]);

    const isValid = useMemo(() => {
        const amount = parseToNumber(amountInput);
        return amount > 0 && !!form.occurredAt && (form.type === 'credit' || form.type === 'debit' || form.type === 'yield');
    }, [amountInput, form]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!isValid) {
            setError('O valor deve ser maior que zero.');
            return;
        }
        try {
            setSubmitting(true);
            const cents = Math.round(parseToNumber(amountInput) * 100);
            await investmentApi.update(investment.id, { 
                amount: cents,
                occurredAt: form.occurredAt,
                type: form.type,
            });
            onSuccess();
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao atualizar o investimento.';
            setError(friendly);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {error && (<Alert type="error" title="Não foi possível concluir" message={error} />)}

            <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Tipo *</label>
                <div className="inline-flex rounded-md shadow-sm" role="group">
                    <button
                        type="button"
                        className={`px-4 py-2 text-sm font-medium border border-secondary-300 ${form.type === 'credit' ? 'bg-primary-600 text-white' : 'bg-white text-secondary-800'} rounded-l-md transition-colors`}
                        onClick={() => setForm({ ...form, type: 'credit' })}
                        aria-pressed={form.type === 'credit'}
                    >
                        Investimento
                    </button>
                    <button
                        type="button"
                        className={`px-4 py-2 text-sm font-medium border-t border-b border-secondary-300 ${form.type === 'debit' ? 'bg-primary-600 text-white' : 'bg-white text-secondary-800'} transition-colors`}
                        onClick={() => setForm({ ...form, type: 'debit' })}
                        aria-pressed={form.type === 'debit'}
                    >
                        Resgate
                    </button>
                    <button
                        type="button"
                        className={`px-4 py-2 text-sm font-medium border-t border-b border-r border-secondary-300 ${form.type === 'yield' ? 'bg-primary-600 text-white' : 'bg-white text-secondary-800'} rounded-r-md transition-colors`}
                        onClick={() => setForm({ ...form, type: 'yield' })}
                        aria-pressed={form.type === 'yield'}
                    >
                        Rendimento
                    </button>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Valor *</label>
                <input
                    type="text"
                    className="input-field"
                    value={amountInput}
                    onChange={(e) => {
                        const raw = e.target.value || '';
                        const numberValue = parseToNumber(raw);
                        setAmountInput(formatBRL(numberValue));
                        setForm({ ...form, amount: numberValue });
                    }}
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                    required
                    autoFocus
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Data *</label>
                <DateInput
                    value={form.occurredAt}
                    onChange={(v) => setForm({ ...form, occurredAt: v || form.occurredAt })}
                    name="occurredAt"
                />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
                <button type="submit" className="btn-primary" disabled={submitting || !isValid}>
                    {submitting ? 'Salvando...' : 'Salvar'}
                </button>
            </div>
        </form>
    );
};

export default InvestmentEditModal;

